import React, { useEffect, useState, useRef } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import AdminLayout from './AdminLayout';
import CategoryManager from './CategoryManager';
import supabase from '../../supabase/supabase';
import { allProducts } from '../../data/catalog';
import './AdminProducts.css';

const {
  FiInbox, FiUpload, FiCheck, FiPlus, FiTrash2, FiEdit2, FiX, FiImage, FiVideo, FiSave, FiTag
} = FiIcons;

function AdminProducts() {
  const [dbProducts, setDbProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [search, setSearch] = useState('');
  const [editProduct, setEditProduct] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showCategories, setShowCategories] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [batchDeleting, setBatchDeleting] = useState(false);
  const [productTab, setProductTab] = useState('live');

  const load = async () => {
    setLoading(true);
    const [{ data }, { data: categoryData }] = await Promise.all([
      supabase.from('products_1750000001').select('*').order('id'),
      supabase.from('product_categories_1750000004').select('*').order('sort_order').order('name')
    ]);
    setDbProducts(data || []);
    setCategories(categoryData || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const seedProducts = async () => {
    setSeeding(true);
    await load();
    setSeeding(false);
    return;
    const rows = allProducts.map((p) => ({
      id: p.id, name: p.name, category: p.category, price: p.price,
      weight: p.weight, image: p.image, available: p.available !== false,
      description: p.description || ''
    }));
    const { error } = await supabase.from('products_1750000001').upsert(rows, {
      onConflict: 'id',
      ignoreDuplicates: true
    });
    if (!error) await load();
    setSeeding(false);
  };

  const createCategory = async (form) => {
    const { data, error } = await supabase
      .from('product_categories_1750000004')
      .insert({
        name: form.name.trim(),
        description: form.description.trim(),
        image_url: form.image_url.trim(),
        active: true,
        sort_order: categories.length + 1
      })
      .select()
      .single();

    if (error) {
      return { error: error.code === '23505' ? 'That category already exists.' : error.message };
    }

    setCategories((prev) => [...prev, data]);
    return { data };
  };

  const updateCategory = async (id, form) => {
    const oldCategory = categories.find((c) => c.id === id);
    const newName = form.name.trim();
    const oldName = oldCategory?.name || '';

    const { error } = await supabase
      .from('product_categories_1750000004')
      .update({
        name: newName,
        description: form.description.trim(),
        image_url: form.image_url.trim(),
        active: form.active,
        updated_at: new Date().toISOString()
      })
      .eq('id', id);

    if (error) return;

    // If category name changed, also update all products using the old name
    if (newName && oldName && newName !== oldName) {
      await supabase
        .from('products_1750000001')
        .update({ category: newName })
        .eq('category', oldName);
    }

    setCategories((prev) =>
      prev.map((c) =>
        c.id === id
          ? { ...c, name: newName, description: form.description.trim(), image_url: form.image_url.trim(), active: form.active }
          : c
      )
    );

    // Reload products if name changed so the table reflects the update
    if (newName && oldName && newName !== oldName) {
      const { data } = await supabase.from('products_1750000001').select('*').order('id');
      setDbProducts(data || []);
    }
  };

  const deleteCategory = async (id) => {
    await supabase.from('product_categories_1750000004').delete().eq('id', id);
    setCategories((prev) => prev.filter((c) => c.id !== id));
  };

  const deleteCategories = async (ids) => {
    await supabase.from('product_categories_1750000004').delete().in('id', ids);
    setCategories((prev) => prev.filter((c) => !ids.includes(c.id)));
  };

  const deleteProduct = async (id) => {
    if (!confirm('Are you sure?\n\nSelect OK for Yes or Cancel for No. The product will be moved to the archive and can be restored later.')) return;
    if (false) {
      if (!confirm('Delete this product permanently?')) return;
      await supabase.from('products_1750000001').delete().eq('id', id);
      setDbProducts((prev) => prev.filter((p) => p.id !== id));
    }
    const archivedAt = new Date().toISOString();
    await supabase
      .from('products_1750000001')
      .update({ archived: true, archived_at: archivedAt, available: false })
      .eq('id', id);
    setDbProducts((prev) => prev.map((p) =>
      p.id === id ? { ...p, archived: true, archived_at: archivedAt, available: false } : p
    ));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const deleteSelectedProducts = async () => {
    if (selectedIds.size === 0) return;
    if (!confirm(`Are you sure?\n\nSelect OK for Yes or Cancel for No. ${selectedIds.size} selected product${selectedIds.size === 1 ? '' : 's'} will be moved to the archive and can be restored later.`)) return;
    if (false) {
      if (!confirm(`Delete ${selectedIds.size} selected product${selectedIds.size === 1 ? '' : 's'} permanently?`)) return;
    }
    setBatchDeleting(true);
    const ids = [...selectedIds];
    if (false) {
      await supabase.from('products_1750000001').delete().in('id', ids);
      setDbProducts((prev) => prev.filter((p) => !ids.includes(p.id)));
    }
    const archivedAt = new Date().toISOString();
    await supabase
      .from('products_1750000001')
      .update({ archived: true, archived_at: archivedAt, available: false })
      .in('id', ids);
    setDbProducts((prev) => prev.map((p) =>
      ids.includes(p.id) ? { ...p, archived: true, archived_at: archivedAt, available: false } : p
    ));
    setSelectedIds(new Set());
    setBatchDeleting(false);
  };

  const restoreProduct = async (product) => {
    const available = Number(product.stock_quantity || 0) > 0;
    await supabase
      .from('products_1750000001')
      .update({ archived: false, archived_at: null, available })
      .eq('id', product.id);
    setDbProducts((prev) => prev.map((p) =>
      p.id === product.id ? { ...p, archived: false, archived_at: null, available } : p
    ));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(product.id);
      return next;
    });
  };

  const restoreSelectedProducts = async () => {
    if (selectedIds.size === 0) return;
    setBatchDeleting(true);
    const ids = [...selectedIds];
    const selectedProducts = dbProducts.filter((product) => ids.includes(product.id));
    await Promise.all(selectedProducts.map((product) =>
      supabase
        .from('products_1750000001')
        .update({
          archived: false,
          archived_at: null,
          available: Number(product.stock_quantity || 0) > 0
        })
        .eq('id', product.id)
    ));
    setDbProducts((prev) => prev.map((p) =>
      ids.includes(p.id)
        ? { ...p, archived: false, archived_at: null, available: Number(p.stock_quantity || 0) > 0 }
        : p
    ));
    setSelectedIds(new Set());
    setBatchDeleting(false);
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filtered.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map((p) => p.id)));
    }
  };

  const toggleAvailability = async (product) => {
    const updated = !product.available;
    await supabase.from('products_1750000001').update({ available: updated }).eq('id', product.id);
    setDbProducts((prev) => prev.map((p) => p.id === product.id ? { ...p, available: updated } : p));
  };

  const filtered = dbProducts.filter((p) =>
    (productTab === 'archive' ? p.archived === true : p.archived !== true) &&
    (
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.category.toLowerCase().includes(search.toLowerCase())
    )
  );

  const allFilteredSelected = filtered.length > 0 && selectedIds.size === filtered.length;

  const changeProductTab = (tab) => {
    setProductTab(tab);
    setSelectedIds(new Set());
  };

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <div><h1>Products</h1><p>Manage your botanical product catalog</p></div>
        <div className="admin-header-actions">
          <input className="admin-search" placeholder="Search products…" value={search} onChange={(e) => setSearch(e.target.value)} />
          <button className="admin-btn secondary" onClick={() => setShowCategories(true)}>
            <SafeIcon icon={FiTag} /> Categories
          </button>
          <button className="admin-btn secondary" onClick={seedProducts} disabled={seeding}>
            <SafeIcon icon={FiUpload} /> {seeding ? 'Refreshing…' : 'Refresh Catalog'}
          </button>
          <button className="admin-btn primary" onClick={() => setShowAdd(true)}>
            <SafeIcon icon={FiPlus} /> Add Product
          </button>
        </div>
      </div>

      <div className="admin-header-actions" style={{ margin: '0 28px 16px' }}>
        <button
          className={`admin-btn ${productTab === 'live' ? 'primary' : 'secondary'}`}
          onClick={() => changeProductTab('live')}
        >
          Live Products
        </button>
        <button
          className={`admin-btn ${productTab === 'archive' ? 'primary' : 'secondary'}`}
          onClick={() => changeProductTab('archive')}
        >
          Archive
        </button>
      </div>

      {selectedIds.size > 0 && (
        <div className="batch-action-bar" style={{ margin: '0 28px 16px' }}>
          <span>{selectedIds.size} product{selectedIds.size === 1 ? '' : 's'} selected</span>
          {false && (
            <button
              className="admin-btn secondary batch-delete-btn"
              onClick={deleteSelectedProducts}
              disabled={batchDeleting}
            >
              <SafeIcon icon={FiTrash2} /> {batchDeleting ? 'Deleting…' : 'Delete Selected'}
            </button>
          )}
          <button
            className="admin-btn secondary batch-delete-btn"
            onClick={productTab === 'archive' ? restoreSelectedProducts : deleteSelectedProducts}
            disabled={batchDeleting}
          >
            <SafeIcon icon={FiTrash2} /> {batchDeleting ? (productTab === 'archive' ? 'Restoring…' : 'Deleting…') : (productTab === 'archive' ? 'Restore Selected' : 'Delete Selected')}
          </button>
        </div>
      )}

      <div className="admin-card">
        {loading ? (
          <div className="admin-loading">Loading…</div>
        ) : filtered.length === 0 ? (
          <>
            {false && (
              <div className="admin-empty"><SafeIcon icon={FiInbox} /><p>No products found. Click "Add Product" to create one.</p></div>
            )}
            <div className="admin-empty">
              <SafeIcon icon={FiInbox} />
              <p>{productTab === 'archive' ? 'No archived products found.' : 'No products found. Click "Add Product" to create one.'}</p>
            </div>
          </>
        ) : (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th style={{ width: 36 }}>
                    <input
                      type="checkbox"
                      checked={allFilteredSelected}
                      onChange={toggleSelectAll}
                      title="Select all"
                    />
                  </th>
                  <th>Image</th><th>Name</th><th>Category</th><th>Price</th><th>Stock</th><th>Images</th><th>Status</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((product) => {
                  const imgCount = [product.image, product.image_2, product.image_3, product.image_4, product.image_5].filter(Boolean).length;
                  return (
                    <tr key={product.id} className={selectedIds.has(product.id) ? 'row-selected' : ''}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(product.id)}
                          onChange={() => toggleSelect(product.id)}
                        />
                      </td>
                      <td><img src={product.image} alt="" className="admin-product-thumb" /></td>
                      <td><strong>{product.name}</strong><br /><small>{product.weight}</small></td>
                      <td>{product.category}</td>
                      <td>Rs. {Number(product.price).toLocaleString()}</td>
                      <td><strong>{Number(product.stock_quantity || 0).toLocaleString()}</strong></td>
                      <td>{imgCount} img{product.video_url ? ' + video' : ''}</td>
                      <td>
                        {productTab === 'archive' ? (
                          <span>Archived</span>
                        ) : (
                          <button className={`admin-action-btn ${product.available ? 'primary' : 'danger'}`} onClick={() => toggleAvailability(product)}>
                            {product.available ? 'In Stock' : 'Out of Stock'}
                          </button>
                        )}
                      </td>
                      <td>
                        <div className="admin-row-actions">
                          {productTab === 'archive' ? (
                            <button className="admin-btn secondary" onClick={() => restoreProduct(product)}>Restore</button>
                          ) : (
                            <>
                              <button className="admin-icon-btn" onClick={() => setEditProduct(product)} title="Edit"><SafeIcon icon={FiEdit2} /></button>
                              <button className="admin-icon-btn danger" onClick={() => deleteProduct(product.id)} title="Delete"><SafeIcon icon={FiTrash2} /></button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {(editProduct || showAdd) && (
        <ProductEditor
          product={editProduct}
          categories={categories}
          onClose={() => { setEditProduct(null); setShowAdd(false); }}
          onSave={async (data) => {
            if (editProduct) {
              await supabase.from('products_1750000001').update(data).eq('id', editProduct.id);
            } else {
              await supabase.from('products_1750000001').insert(data);
            }
            setEditProduct(null);
            setShowAdd(false);
            await load();
          }}
        />
      )}

      {showCategories && (
        <CategoryManager
          categories={categories}
          onClose={() => setShowCategories(false)}
          onCreate={createCategory}
          onUpdate={updateCategory}
          onDelete={deleteCategory}
          onDeleteMany={deleteCategories}
        />
      )}
    </AdminLayout>
  );
}

function ProductEditor({ product, categories, onClose, onSave }) {
  const [form, setForm] = useState({
    name: product?.name || '',
    category: product?.category || '',
    price: product?.price || 0,
    stock_quantity: product?.stock_quantity || 0,
    weight: product?.weight || '',
    description: product?.description || '',
    image: product?.image || '',
    image_2: product?.image_2 || '',
    image_3: product?.image_3 || '',
    image_4: product?.image_4 || '',
    image_5: product?.image_5 || '',
    video_url: product?.video_url || '',
    available: product?.available !== false
  });
  const [saving, setSaving] = useState(false);
  const [videoPreviewError, setVideoPreviewError] = useState(false);

  const update = (key) => (e) => {
    if (key === 'video_url') setVideoPreviewError(false);
    setForm((f) => ({ ...f, [key]: e.target.value }));
  };

  const handleFileUpload = (key) => async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setForm((f) => ({ ...f, [key]: ev.target.result }));
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    const stockQuantity = Math.max(0, Math.floor(Number(form.stock_quantity) || 0));
    setSaving(true);
    await onSave({
      ...form,
      price: Number(form.price),
      stock_quantity: stockQuantity,
      available: stockQuantity > 0 && form.available
    });
    setSaving(false);
  };

  const imageFields = [
    { key: 'image', label: 'Main Image' },
    { key: 'image_2', label: 'Image 2' },
    { key: 'image_3', label: 'Image 3' },
    { key: 'image_4', label: 'Image 4' },
    { key: 'image_5', label: 'Image 5' }
  ];

  const directVideo = /\.(mp4|webm|ogg|mov|m4v)(\?.*)?$/i.test(form.video_url.trim());

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
        <div className="admin-modal-header">
          <h2>{product ? 'Edit Product' : 'Add Product'}</h2>
          <button className="admin-icon-btn" onClick={onClose}><SafeIcon icon={FiX} /></button>
        </div>
        <div className="admin-modal-body">
          <div className="editor-grid">
            <div className="editor-fields">
              <div className="editor-field">
                <label>Name</label>
                <input value={form.name} onChange={update('name')} placeholder="Product name" />
              </div>
              <div className="editor-row-2">
                <div className="editor-field">
                  <label>Category</label>
                  <select value={form.category} onChange={update('category')}>
                    <option value="">Select category</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.name}>{category.name}</option>
                    ))}
                  </select>
                </div>
                <div className="editor-field">
                  <label>Price (Rs.)</label>
                  <input type="number" value={form.price} onChange={update('price')} />
                </div>
              </div>
              <div className="editor-row-2">
                <div className="editor-field">
                  <label>Weight</label>
                  <input value={form.weight} onChange={update('weight')} placeholder="e.g. 100g" />
                </div>
                <div className="editor-field">
                  <label>Stock Quantity</label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={form.stock_quantity}
                    onChange={update('stock_quantity')}
                    placeholder="Number available"
                  />
                </div>
              </div>
              <div className="editor-field">
                <label>Description</label>
                <textarea value={form.description} onChange={update('description')} rows={4} placeholder="Product description…" />
              </div>
              <div className="editor-field">
                <label>Video URL (optional)</label>
                <input value={form.video_url} onChange={update('video_url')} placeholder="https://youtube.com/embed/..." />
              </div>
              {form.video_url && (
                <div style={{ overflow: 'hidden', border: '1px solid #e5e7eb', borderRadius: 10, background: '#f8fafc' }}>
                  {videoPreviewError ? (
                    <p style={{ margin: 0, padding: 12, color: '#b45309', fontSize: '0.78rem' }}>
                      This video host blocked embedded playback. Use a direct video URL from a host that allows embedding.
                    </p>
                  ) : directVideo ? (
                    <video
                      src={form.video_url}
                      controls
                      playsInline
                      preload="metadata"
                      referrerPolicy="no-referrer"
                      onError={() => setVideoPreviewError(true)}
                      style={{ display: 'block', width: '100%', maxHeight: 220, background: '#111827' }}
                    />
                  ) : (
                    <iframe
                      src={form.video_url}
                      title="Product video preview"
                      allowFullScreen
                      referrerPolicy="no-referrer"
                      sandbox="allow-scripts allow-same-origin allow-presentation"
                      onError={() => setVideoPreviewError(true)}
                      style={{ display: 'block', width: '100%', height: 220, border: 0 }}
                    />
                  )}
                </div>
              )}
              <label className="editor-checkbox">
                <input type="checkbox" checked={form.available} onChange={(e) => setForm((f) => ({ ...f, available: e.target.checked }))} />
                Available / In Stock
              </label>
            </div>
            <div className="editor-images">
              <h3>Images</h3>
              {imageFields.map(({ key, label }) => (
                <div className="editor-image-field" key={key}>
                  <label>{label}</label>
                  {form[key] && <img src={form[key]} alt={label} className="editor-image-preview" />}
                  <div className="editor-image-inputs">
                    <input value={form[key]} onChange={update(key)} placeholder="Image URL" />
                    <label className="file-upload-btn">
                      <SafeIcon icon={FiUpload} /> File
                      <input type="file" accept="image/*" onChange={handleFileUpload(key)} hidden />
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="admin-modal-footer">
          <button className="admin-btn secondary" onClick={onClose}>Cancel</button>
          <button className="admin-btn primary" onClick={handleSave} disabled={saving}>
            <SafeIcon icon={FiSave} /> {saving ? 'Saving…' : 'Save Product'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AdminProducts;